import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { formatQuantity } from '../../utils';
import { numFont, plateFont } from '../../utils/fonts';

/**
 * Open work order lines of a part, each with a "from own stock / ordered for this job"
 * choice. Shown in the Varasto dialogs before stock is booked for a part that open jobs
 * already carry, so the workshop can say which of those pieces the entered balance
 * covers. A line marked "ordered for this job" neither reserves stock nor deducts it on
 * completion (MaintenancePart.reduce_from_inventory = false).
 *
 * useOpenWorkOrderLines(partId, enabled) loads the lines and keeps the choices;
 * call save() before the stock action so a failed PATCH stops the dialog cleanly.
 */
export function useOpenWorkOrderLines(partId, enabled = true) {
  const [lines, setLines] = useState([]);
  const [choices, setChoices] = useState({});

  useEffect(() => {
    let cancelled = false;
    if (!partId || !enabled) {
      setLines([]);
      setChoices({});
      return undefined;
    }
    axios
      .get(`/api/parts/${partId}/open-work-order-lines/`)
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        setLines(rows);
        const initial = {};
        rows.forEach((row) => {
          initial[row.maintenance_part_id] = row.reduce_from_inventory !== false;
        });
        setChoices(initial);
      })
      .catch(() => {
        // Best effort: a failed lookup must never block booking stock.
        if (!cancelled) {
          setLines([]);
          setChoices({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [partId, enabled]);

  const setChoice = useCallback((maintenancePartId, fromOwnStock) => {
    setChoices((prev) => ({ ...prev, [maintenancePartId]: fromOwnStock }));
  }, []);

  const reset = useCallback(() => {
    const initial = {};
    lines.forEach((row) => {
      initial[row.maintenance_part_id] = row.reduce_from_inventory !== false;
    });
    setChoices(initial);
  }, [lines]);

  // PATCH only the lines whose choice changed; rejects on the first failure.
  const save = useCallback(async () => {
    const changed = lines.filter(
      (row) => (row.reduce_from_inventory !== false) !== (choices[row.maintenance_part_id] !== false)
    );
    await Promise.all(
      changed.map((row) =>
        axios.patch(`/api/maintenances/${row.maintenance_id}/update_part/${row.maintenance_part_id}/`, {
          reduce_from_inventory: choices[row.maintenance_part_id] !== false,
        })
      )
    );
    return changed.length;
  }, [lines, choices]);

  return { lines, choices, setChoice, save, reset };
}

export default function OpenWorkOrderLinesChoice({ lines, choices, onChoice, sx }) {
  const { t } = useTranslation();
  if (!lines || lines.length === 0) return null;
  return (
    <Box
      sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: '8px', ...sx }}
      data-testid="open-work-order-lines"
    >
      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
        {t('inventory.openLinesTitle')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t('inventory.openLinesHelp')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {lines.map((row) => {
          const fromOwnStock = choices[row.maintenance_part_id] !== false;
          return (
            <Box
              key={row.maintenance_part_id}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}
              data-testid={`open-work-order-line-${row.maintenance_part_id}`}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <Box component="span" sx={{ ...numFont, fontWeight: 600 }}>#{row.work_number}</Box>
                  {row.registration_number && (
                    <Box component="span" sx={{ ...plateFont, fontWeight: 600 }}>{row.registration_number}</Box>
                  )}
                  <Box component="span" sx={{ ...numFont }}>
                    {formatQuantity(row.quantity)} {t('inventory.pcsShort')}
                  </Box>
                </Typography>
                {row.customer_name && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 260 }}>
                    {row.customer_name}
                  </Typography>
                )}
              </Box>
              <ToggleButtonGroup
                value={fromOwnStock ? 'stock' : 'ordered'}
                exclusive
                size="small"
                onChange={(e, val) => {
                  if (val) onChoice(row.maintenance_part_id, val === 'stock');
                }}
              >
                <ToggleButton
                  value="stock"
                  sx={{ textTransform: 'none', fontSize: '12px', py: 0.25 }}
                  data-testid={`open-line-stock-${row.maintenance_part_id}`}
                >
                  {t('inventory.fromOwnStock')}
                </ToggleButton>
                <ToggleButton
                  value="ordered"
                  sx={{ textTransform: 'none', fontSize: '12px', py: 0.25 }}
                  data-testid={`open-line-ordered-${row.maintenance_part_id}`}
                >
                  {t('inventory.orderedForJob')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
